import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { BusyService } from '../_services/busy.service';

let lastShownMessage = '';
let lastShownTime = 0;

function isInternalApi(url: string): boolean {
  if (!url) return false;
  // Relative URLs like '/api/...', 'api/...' are internal
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || !url.startsWith('http')) {
    return true;
  }
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return (
      hostname.endsWith('chemrobotics.com') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1'
    );
  } catch {
    return false;
  }
}

function parseErrorMessage(rawError: any, status?: number): string {
  let errorMsg = 'An error occurred while processing your request.';
  if (!rawError) return errorMsg;

  if (typeof rawError === 'string') {
    const trimmed = rawError.trim();
    if (trimmed.startsWith('<') || trimmed.includes('<!DOCTYPE') || trimmed.includes('<html')) {
      const preMatch = trimmed.match(/<pre>(.*?)<\/pre>/is);
      if (preMatch && preMatch[1]) {
        errorMsg = preMatch[1].replace(/<[^>]*>/g, '').trim();
      } else {
        const stripped = trimmed.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        if (stripped && stripped.length < 150) {
          errorMsg = stripped;
        } else {
          errorMsg = status ? `HTTP Error ${status}` : 'Server Error';
        }
      }
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        errorMsg = parsed.message || parsed.error || trimmed;
      } catch {
        errorMsg = trimmed;
      }
    }
  } else if (typeof rawError === 'object') {
    errorMsg = rawError.message || rawError.error || rawError.detail || errorMsg;
  }

  return errorMsg;
}

function isTokenRelatedMessage(msg: string): boolean {
  if (!msg || typeof msg !== 'string') return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes('token') ||
    lower.includes('jwt') ||
    lower.includes('unauthorized') ||
    lower.includes('unauthenticated') ||
    lower.includes('auth token') ||
    lower.includes('not logged in')
  );
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const busyService = inject(BusyService);

  // 1. Bypass 3rd party / external APIs (e.g. ipapi.co, PubChem, Google, etc.)
  // Do not send internal Authorization headers to 3rd party domains.
  // Do not intercept or show global error/quota toasts for 3rd party API failures.
  if (!isInternalApi(req.url)) {
    return next(req);
  }

  let currentuser: any = localStorage.getItem('currentUser');
  let userdata = currentuser ? JSON.parse(currentuser) : null;
  const isLoggedIn = !!(userdata && (userdata.token || userdata.access_token));

  // 2. Attach Authorization header for internal endpoints when user is logged in
  if (isLoggedIn) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${userdata.token || userdata.access_token}`
      }
    });
  }

  const isAuthEndpoint =
    req.url.includes('/auth-login') ||
    req.url.includes('/login') ||
    req.url.includes('/auto-login') ||
    req.url.includes('/refresh-token');

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const body: any = event.body;
        if (
          body &&
          (body.status === false ||
            body.status === 'false' ||
            body.code == 429 ||
            body.status === 'error' ||
            (typeof body.message === 'string' && body.message.toLowerCase().includes('quota')))
        ) {
          const rawMsg =
            body.message ||
            body.error ||
            (body.code == 429 ? 'Quota Exceeded: You have reached your search limit.' : 'An error occurred.');
          const errorMsg = parseErrorMessage(rawMsg);
          const lowerMsg = (typeof errorMsg === 'string' ? errorMsg : '').toLowerCase();

          // If user is not logged in and message is token-related or unauthorized, suppress completely (do not toast)
          if (!isLoggedIn && (isTokenRelatedMessage(lowerMsg) || lowerMsg.includes('unauthorized') || lowerMsg.includes('forbidden') || lowerMsg.includes('access denied'))) {
            return;
          }

          // Suppress raw technical token validation messages from toasts ("or mt bta")
          if (isTokenRelatedMessage(lowerMsg)) {
            return;
          }

          const isInactiveOrSuspended =
            lowerMsg.includes('suspended') ||
            lowerMsg.includes('inactive') ||
            lowerMsg.includes('account suspended') ||
            lowerMsg.includes('account is inactive');

          if (errorMsg) {
            busyService.idle();
            const now = Date.now();
            if (lastShownMessage !== errorMsg || now - lastShownTime > 1000) {
              lastShownMessage = errorMsg;
              lastShownTime = now;
              const isQuota = body.code == 429 || (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('quota'));
              messageService.add({
                severity: isQuota ? 'warn' : 'error',
                summary: isQuota ? 'Quota Exceeded' : (isInactiveOrSuspended ? 'Account Inactive' : 'Error'),
                detail: errorMsg,
                life: 7000
              });
            }
          }

          // Only clear session & redirect if the user was actually logged in and account is suspended/inactive
          if (isInactiveOrSuspended && !isAuthEndpoint && isLoggedIn) {
            localStorage.clear();
            sessionStorage.clear();
            if (window.location.pathname !== '/') {
              setTimeout(() => {
                window.location.href = '/';
              }, 1500);
            }
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      busyService.idle();

      // Do not show toasts for 404 Not Found (missing routes/resources)
      if (error.status === 404) {
        return throwError(() => error);
      }

      let errorMsg = parseErrorMessage(error.error || error.message, error.status);
      const lowerMsg = (typeof errorMsg === 'string' ? errorMsg : '').toLowerCase();

      // If user is not logged in and error is 401/403 or token-related, silently ignore without showing toast
      if (!isLoggedIn && (error.status === 401 || error.status === 403 || isTokenRelatedMessage(lowerMsg) || lowerMsg.includes('unauthorized') || lowerMsg.includes('forbidden') || lowerMsg.includes('access denied'))) {
        return throwError(() => error);
      }

      // Suppress raw technical token validation messages from toasts ("or mt bta")
      if (isTokenRelatedMessage(lowerMsg)) {
        return throwError(() => error);
      }

      const isQuota = error.status === 429 || (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('quota'));
      const isInactiveOrSuspended =
        lowerMsg.includes('suspended') ||
        lowerMsg.includes('inactive') ||
        lowerMsg.includes('account suspended') ||
        lowerMsg.includes('account is inactive');

      let summary = isQuota ? 'Quota Exceeded' : (isInactiveOrSuspended ? 'Account Inactive' : 'Error');

      const now = Date.now();
      if (lastShownMessage !== errorMsg || now - lastShownTime > 1000) {
        lastShownMessage = errorMsg;
        lastShownTime = now;
        messageService.add({
          severity: isQuota ? 'warn' : 'error',
          summary: summary,
          detail: errorMsg,
          life: 7000
        });
      }

      // Only redirect if user was logged in and account is specifically suspended/inactive
      if (isInactiveOrSuspended && !isAuthEndpoint && isLoggedIn) {
        localStorage.clear();
        sessionStorage.clear();
        if (window.location.pathname !== '/') {
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        }
      }

      return throwError(() => error);
    })
  );
};
